import React from 'react'
import ReactTable from 'react-table-6'
import { I18n } from 'react-redux-i18n'

import { formatCurrency, formatProfitLoss, formatPrice } from '../lib/currency-formatter'
import { closedPositionsQuery } from '../graphql/schema'
import { Prices, TradeCommandType } from '@bdswiss/mt4-connector'
import PositionClosed from './PositionClosed'
import './PositionsTable.css'
import moment from 'moment-timezone'
const loadingImage = require('../images/spinner.gif').default

export interface Props {
  assets: Prices
  history: closedPositionsQuery
  currency: string
  locale: string
  favourites: string[]
  pageSize?: number
  onShowChart: (symbol: string, tradeType: TradeCommandType) => void
  onGetClosedPositions: (limit: number) => void
  onToggleFavourite: (favourites: string[]) => void
}

export interface State {
  filter: string
  favourites: { [symbol: string]: { confirmed: boolean; operation: 'on' | 'off' } } // true if confirmed from server
  loadingData: boolean
}

const HistoryTable = (props: Props) => {
  return <HistoryTableContent {...props} />
}

class HistoryTableContent extends React.Component<Props, State> {
  private columns = [
    {
      expander: true,
      headerStyle: { display: 'none' },
      width: 0,
    },
    {
      style: { textOverflow: 'clip' },
      width: 22,
      Cell: (props: any) => {
        return (
          <div className={'wt-expander wte' + (props.isExpanded ? '-down' : '-right')}>&nbsp;</div>
        )
      },
    },
    {
      minWidth: 20,
      sortable: false,
      filterable: false,
      className: 'favourite-cell',
      Cell: (props: any) => {
        const favourite = Object.keys(this.state.favourites).find(
          key => key === props.original.symbol
        )
        const confirmed = favourite ? this.state.favourites[favourite].confirmed : true
        return (
          <i
            onClick={() => this.toggleFavourite(props.original.symbol)}
            className={`favourite ${favourite ? 'icon-star-full' : 'icon-star-empty'} ${
              !confirmed ? 'unconfirmed' : ''
            }`}
            title="Favourite"
            data-cy={props.value}
          />
        )
      },
    },
    {
      Header: (props: any) => <span>{I18n.t('asset')}</span>,
      minWidth: 40,
      Cell: (props: any) => {
        const { symbol } = props.original
        const { label } = this.props.assets[symbol] as any
        return (
          <div title={label} className="bdswiss-table--bold">
            {label}
          </div>
        )
      },
    },
    {
      Header: (props: any) => <span>{I18n.t('orderId')}</span>,
      minWidth: 40,
      Cell: (props: any) => {
        return <div>{props.original.id}</div>
      },
    },
    {
      Header: (props: any) => <span>{I18n.t('direction')}</span>,
      minWidth: 30,
      Cell: (props: any) => {
        const { type } = props.original
        return (
          <div
            className={/buy/i.test(type) ? 'bdswiss-table--positive' : 'bdswiss-table--negative'}
          >
            <i className={/buy/i.test(type) ? 'icon-buy-new' : 'icon-sell-new'} />
            {I18n.t(type)}
          </div>
        )
      },
    },
    {
      Header: (props: any) => <span>{I18n.t('value')}</span>,
      minWidth: 30,
      headerClassName: 'text-right',
      className: 'text-right',
      Cell: (props: any) => {
        const { investment, volume, symbol } = props.original
        const { locale, currency } = this.props
        const investmentValue = investment
          ? formatCurrency(investment, currency, locale)
          : `${volume} ${I18n.t('lots', { count: volume === 1 ? 1 : 0 })}`
        const asset = this.props.assets[symbol]
        const value = asset
          ? formatCurrency(volume * asset.lotSize, asset.contractType, this.props.locale)
          : investmentValue
        return <div>{value.replace(/0+$/g, '').replace(/\.$/, '.00')}</div>
      },
    },
    {
      Header: (props: any) => <span>{I18n.t('profit')}</span>,
      minWidth: 40,
      headerClassName: 'text-right',
      className: 'text-right',
      Cell: (props: any) => {
        const { profit } = props.original
        const { currency, locale } = this.props
        return (
          <div className={'bdswiss-table--' + (profit > 0 ? 'positive' : 'negative')}>
            {formatProfitLoss(profit, currency, locale, currency === 'CUD' ? {precision: 2} : {})}
          </div>
        )
      },
    },
    // {
    //   Header: (props:any) =>  <span>{I18n.t('leverage')}</span>,
    //   minWidth: 40,
    //   headerClassName: 'text-right',
    //   className: 'text-right',
    //   Cell: (props:any) => {
    //     const {leverage} = props.original;
    //     return (
    //       leverage ? <div>1:{leverage}</div> : undefined
    //     )
    //   }
    // },
    {
      Header: (props: any) => <span>{I18n.t('openPrice')}</span>,
      minWidth: 40,
      headerClassName: 'text-right',
      className: 'text-right',
      Cell: (props: any) => {
        const asset = this.props.assets[props.original.symbol]
        const precision = asset ? asset.digits : 5
        return <div>{formatPrice(props.original.openPrice, precision, '')}</div>
      },
    },
    {
      Header: (props: any) => (
        <span>{I18n.t('closePrice')}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
      ),
      minWidth: 40,
      headerClassName: 'text-right',
      className: 'text-right',
      Cell: (props: any) => {
        const asset = this.props.assets[props.original.symbol]
        const precision = asset ? asset.digits : 5
        return (
          <div>
            {formatPrice(props.original.closePrice, precision, '')}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </div>
        )
      },
    },
    {
      id: 'closeTime',
      accessor: 'closeTime',
      Header: (props: any) => <span>{I18n.t('closeDate')}</span>,
      minWidth: 60,
      Cell: (props: any) => {
        moment.locale(this.props.locale)
        const dateTime = moment(props.original.closeTime)
        return (
          <div>
            <div>
              {dateTime.format("MMM DD 'YY")}&nbsp;&nbsp;&nbsp;&nbsp;
              {this.props.locale === 'en' ? dateTime.format('hh:mmA') : dateTime.format('HH:mm')}
            </div>
          </div>
        )
      },
    },
  ]

  constructor(props: Props) {
    super(props)

    this.toggleFavourite = this.toggleFavourite.bind(this)

    this.state = {
      filter: 'active',
      favourites: this.props.favourites.reduce(
        (acc, cur) => {
          acc[cur] = { confirmed: true, operation: 'on' }
          return acc
        },
        {} as any
      ),
      loadingData: false,
    }
  }

  public render() {
    const { assets, currency, locale, onShowChart } = this.props

    return (
      <div>
        <ReactTable
          noDataText={I18n.t('noItemsToDisplay')}
          data={this.props.history.closedPositions!}
          columns={this.columns}
          className="bdswiss-table"
          showPagination={false}
          sortable={false}
          resizable={false}
          collapseOnDataChange={false}
          minRows={15}
          pageSize={this.props.history.closedPositions!.length}
          defaultSorted={[
            {
              id: 'closeTime',
              desc: true,
            },
          ]}
          getTrProps={(state: any, rowInfo: any, col: any, instance: any) => {
            return {
              onClick: (e: any) => {
                if (/favourite/i.test(e.target.className)) {
                  return
                }
                const { expanded } = state
                const path = rowInfo.nestingPath[0]
                const diff = { [path]: expanded[path] ? false : true }

                instance.setState({
                  expanded: {
                    ...expanded,
                    ...diff,
                  },
                })
              },
            }
          }}
          SubComponent={row => {
            return (
              <PositionClosed
                position={row.original}
                asset={assets[row.original.symbol]}
                currency={currency}
                locale={locale}
                onShowChart={onShowChart}
              />
            )
          }}
        />
        <div
          className="loadingData"
          style={{ display: `${this.state.loadingData ? 'block' : 'none'}` }}
        >
          <img alt="Loading..." src={loadingImage} />
        </div>
      </div>
    )
  }

  public componentDidMount() {
    const page = this.props.pageSize || 100
    let limit = page
    this.props.onGetClosedPositions(limit)
    const content = document.getElementById('positionsPaneContent')!
    content.addEventListener('scroll', e => {
      const { scrollHeight, clientHeight, scrollTop } = e.target as any
      const dataLength = this.props.history.closedPositions!.length
      // console.log(scrollHeight, scrollTop, clientHeight, limit, dataLength);
      if (scrollHeight - scrollTop <= clientHeight + 30) {
        if (limit < dataLength + page) {
          this.setState({ loadingData: true })
          limit = limit + page
          this.props.onGetClosedPositions(limit)
        } else {
          this.setState({ loadingData: false })
        }
      }
      if (scrollTop === 0 && limit > page) {
        limit = page
        this.props.onGetClosedPositions(limit)
      }
    })
  }

  public componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevProps.favourites !== this.props.favourites) {
      this.setState(pState => ({
        favourites: this.props.favourites.reduce(
          (acc, cur) => {
            acc[cur] = { confirmed: true, operation: 'on' }
            return acc
          },
          {} as any
        ),
      }))
    }
  }

  private toggleFavourite(favourite: string) {
    const { favourites } = this.state
    if (Object.keys(favourites).includes(favourite)) {
      favourites[favourite] = { confirmed: false, operation: 'off' }
    } else {
      favourites[favourite] = { confirmed: false, operation: 'on' }
    }
    this.props.onToggleFavourite(
      Object.keys(favourites).filter(key => favourites[key].operation === 'on')
    )
    this.setState(prevState => ({
      favourites,
    }))
  }
}

export default HistoryTable
